import { useQueryClient } from '@tanstack/vue-query';
import { io } from 'socket.io-client';
import { computed, type ComputedRef, onMounted, onUnmounted, ref } from 'vue';
import { realtimeApi } from '../api/realtime.api.js';
import { analysisQueueQueryKey, analysisRunsQueryKey } from '../queries.js';
import { QUEUE_REALTIME_EVENTS, type RealtimeStatus } from './realtime-connection.js';
import { createRealtimeLink } from './realtime-link.js';

/**
 * The queue, live.
 *
 * Three pieces meet here and nothing else does: socket.io, vue-query and the component's
 * lifecycle. Every decision — when to retry, how long to wait, what to say — is in
 * `realtime-connection.ts` and `realtime-link.ts`, which have specs. This file is wiring, and it
 * is kept boring on purpose: `.vue` files are not unit-tested in this repository, and a rule
 * that ends up in one is a rule nobody checks.
 *
 * ## One connection, however many things watch it
 *
 * It used to build a socket per caller, which was fine while the queue screen was the only one.
 * It is not any more — the Virtual Profiler home reads it too, and the connection indicator is
 * wanted somewhere permanent so it can be seen from any screen. Three callers would have meant
 * three sockets, three tickets a minute during an outage, and three answers to «¿estamos
 * conectados?» that could disagree.
 *
 * So the link is shared and reference-counted: the first watcher opens it, the last one to go
 * closes it. A watcher that never unmounts — something in the shell — simply keeps it open for as
 * long as the tab lives, which is the behaviour that makes an always-visible indicator honest.
 *
 * ## Why socket.io's own reconnection is switched off
 *
 * Because it would work, and that is the trap. It reconnects presenting the handshake it was
 * built with, and our handshake carries a **single-use** ticket that the first connection already
 * spent — so every automatic retry would be refused, for an hour, silently. The retry loop is
 * ours precisely so that each attempt starts by minting a new ticket. `handshake.auth` is also
 * not something a `reconnect_attempt` listener can refresh, since minting is a request and that
 * listener cannot await one.
 *
 * ## Why no cookies travel
 *
 * Virtual Profiler is another origin and accepts a browser session by cookie too, but not ours:
 * this cookie belongs to the back-office. The ticket is the whole credential, so
 * `withCredentials` stays off and there is no CORS negotiation to lose.
 */
export interface QueueRealtime {
  status: ComputedRef<RealtimeStatus>;
  /** The last failure, already translated. Null while nothing is wrong. */
  error: ComputedRef<string | null>;
}

/**
 * The one connection, and who is still watching it.
 *
 * Module scope on purpose: it outlives any single component, which is the whole point. `watchers`
 * is what decides when it may close — not «the queue screen left», because the queue screen is no
 * longer the only thing looking.
 */
let shared: { link: ReturnType<typeof createRealtimeLink>; watchers: number } | null = null;

/** Published to every watcher at once, so two indicators can never disagree. */
const status = ref<RealtimeStatus>('connecting');
const error = ref<string | null>(null);

export function useQueueRealtime(): QueueRealtime {
  const queryClient = useQueryClient();

  const build = (): ReturnType<typeof createRealtimeLink> =>
    createRealtimeLink({
      mintTicket: () => realtimeApi.ticket(),

      open: (ticket, handlers) => {
        const socket = io(ticket.url, {
          auth: { ticket: ticket.ticket },
          reconnection: false,
        });

        socket.on('connect', () => handlers.onOpen());
        socket.on('connect_error', (cause) => handlers.onClosed(cause));
        socket.on('disconnect', (reason) => {
          // Our own `close()` comes back through here. Treating it as a failure would make
          // leaving the screen schedule a reconnection to a screen that no longer exists.
          if (reason === 'io client disconnect') return;

          handlers.onClosed(new Error(reason));
        });

        for (const event of QUEUE_REALTIME_EVENTS) {
          socket.on(event, () => handlers.onEvent());
        }

        return {
          close: () => {
            // Listeners first: `close()` fires `disconnect`, and a handler that runs after the
            // link has moved on is the source of every phantom reconnection.
            socket.removeAllListeners();
            socket.close();
          },
        };
      },

      /**
       * What an event is for, and the only thing it is for.
       *
       * The payload is ignored and never read. The screen is refreshed from the two reads it is
       * made of — the queue, and the ledger its one table lists — so a lost event delays the
       * refresh and cannot corrupt it. Every page of the ledger, not only the one on screen: they
       * are cached by page and filters, and a run that changed state moves between them.
       */
      invalidate: () => {
        void queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey });
        void queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey });
      },

      publish: (next, message) => {
        status.value = next;
        error.value = message;
      },
    });

  onMounted(() => {
    if (!shared) {
      shared = { link: build(), watchers: 0 };
      shared.link.start();
    }

    shared.watchers += 1;
  });

  onUnmounted(() => {
    if (!shared) return;

    shared.watchers -= 1;

    if (shared.watchers > 0) return;

    shared.link.stop();
    shared = null;

    // Back to where it started, so the next watcher does not inherit the last one's failure and
    // spend a moment claiming a connection that has not been attempted yet.
    status.value = 'connecting';
    error.value = null;
  });

  return {
    status: computed(() => status.value),
    error: computed(() => error.value),
  };
}
